<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { EFFICIENCY_TIERS, TIER_NAMES } from '../model/efficiency'
import { MONITOR_SIZES, MONITOR_WATTS } from '../model/parts'
import { isValidPrice, LIMITS, type Usage } from '../model/spec'
import CountSelect from './CountSelect.vue'
import FormRow from './FormRow.vue'
import FormSection from './FormSection.vue'
import PartLinePicker from './PartLinePicker.vue'

/** How the computer is used: only the energy estimate depends on any of this. */
const props = defineProps<{ modelValue: Usage }>()
const emit = defineEmits<{ 'update:modelValue': [Usage] }>()

const { t } = useI18n()

function update<K extends keyof Usage>(key: K, value: Usage[K]): void {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

// Gaming hours are a part of the hours on, so lowering one can pull down the other.
function onHours(hoursPerDay: number): void {
  const heavyHoursPerDay = Math.min(props.modelValue.heavyHoursPerDay, hoursPerDay)
  emit('update:modelValue', { ...props.modelValue, hoursPerDay, heavyHoursPerDay })
}

function onTier(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const tier = EFFICIENCY_TIERS.find((candidate) => candidate === target.value)
  if (tier) update('efficiency', tier)
}

// As in the custom wattage field: blank is NaN, and v-model may hand back a number.
const parsePrice = (value: string | number): number =>
  String(value).trim() === '' ? NaN : Number(value)

// A draft, so a cleared field stays cleared.
const price = ref<string | number>(String(props.modelValue.pricePerKwh))
watch(
  () => props.modelValue.pricePerKwh,
  (value) => {
    if (!Object.is(parsePrice(price.value), value)) price.value = String(value)
  },
)
const priceInvalid = computed(() => !isValidPrice(parsePrice(price.value)))
</script>

<template>
  <FormSection :legend="t('form.usage')">
    <FormRow :label="t('form.hoursPerDay')" for-id="hours">
      <CountSelect
        id="hours"
        :model-value="modelValue.hoursPerDay"
        :min="LIMITS.minHoursPerDay"
        :max="LIMITS.hoursPerDay"
        :label="t('form.hoursPerDay')"
        @update:model-value="onHours"
      />
      <span class="form-row__muted">{{ t('form.hoursUnit') }}</span>
    </FormRow>

    <FormRow :label="t('form.heavyHoursPerDay')" for-id="heavy-hours">
      <CountSelect
        id="heavy-hours"
        :model-value="modelValue.heavyHoursPerDay"
        :max="modelValue.hoursPerDay"
        :label="t('form.heavyHoursPerDay')"
        @update:model-value="(hours) => update('heavyHoursPerDay', hours)"
      />
      <span class="form-row__muted">{{ t('form.hoursUnit') }}</span>
    </FormRow>

    <PartLinePicker
      id="monitors"
      :model-value="modelValue.monitors"
      :label="t('form.monitors')"
      :types="MONITOR_SIZES"
      label-prefix="monitor"
      :watts="MONITOR_WATTS"
      :max="LIMITS.monitors"
      @update:model-value="(monitors) => update('monitors', monitors)"
    />

    <FormRow :label="t('form.efficiency')" for-id="efficiency">
      <select id="efficiency" :value="modelValue.efficiency" @change="onTier">
        <option v-for="tier in EFFICIENCY_TIERS" :key="tier" :value="tier">
          {{ TIER_NAMES[tier] }}
        </option>
      </select>
    </FormRow>

    <FormRow :label="t('form.price')" for-id="price">
      <input
        id="price"
        v-model="price"
        class="usage-form__price mono"
        type="number"
        min="0"
        step="0.01"
        inputmode="decimal"
        autocomplete="off"
        :aria-invalid="priceInvalid"
        :aria-describedby="priceInvalid ? 'price-error' : 'price-hint'"
        @input="update('pricePerKwh', parsePrice(price))"
      />
      <span id="price-hint" class="form-row__muted">{{ t('form.perKwh') }}</span>
      <template #after>
        <p v-if="priceInvalid" id="price-error" class="form-row__error" role="alert">
          {{ t('form.priceInvalid') }}
        </p>
      </template>
    </FormRow>
  </FormSection>
</template>

<style scoped>
.usage-form__price {
  width: 7rem;
}
</style>
